import * as React from 'react';
import myAccountBanner from 'assets/myAccountBanner.svg';
import { useLocation, useNavigate } from "react-router-dom";
import { selectUser, updateCurrentAccount, unsetUser, selectCurrentAccount } from 'store/userSlice';
import { useSelector, useDispatch } from 'react-redux';
import authService from 'services/AuthService';
import styles from './Layout.module.css';
import powerIcon from 'assets/power-icon.svg';
import gasIcon from 'assets/gas-icon.svg';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faHouse, faPowerOff } from '@fortawesome/free-solid-svg-icons';
import {
    AccountServiceInfo,
    EuropaAccountServiceStatus,
    ServiceType
} from 'data/CustomerPortalTypes';
import { ProdHostNames } from 'data/CustomerPortalTypes';
import links from 'data/Links';
import { Popover, Modal } from "antd";
import Search from "antd/lib/input/Search";
import Highlighter from "react-highlight-words";
import { useMemo } from "react";
import { isMobileAndTablet } from "utilities/Utility";

type LayoutPropsType = {
    showBackgroundImage?: boolean,
    hideLogout?: boolean,
    hideAccountSelection?: boolean,
    hideReturnDashboard?: boolean,
    title?: string,
    subtitle?: string,
    routed?: boolean
};

const navbarBgColor = ProdHostNames.indexOf(window.location.hostname.toLowerCase()) > -1
    ? {}
    : { backgroundColor: 'rgb(168, 193, 178)' };

const RoutedLayout = (props: React.PropsWithChildren<LayoutPropsType>) => {
    const dispatch = useDispatch();
    const user = useSelector(selectUser);
    const currentAccount = useSelector(selectCurrentAccount);
    const [accountSelectModalVisible, setAccountSelectModalVisible] = React.useState(false);
    const [searchText, setSearchText] = React.useState("");
    const {
        Switched,
        PendingSwitchOut,
        AccountConsolidation,
        AccountClosedToWorkAroundSystem,
        AccountDisconnectedNonPayment,
        Closed
    } = EuropaAccountServiceStatus

    const statusMap = {
        green:
            {
                serviceStatus: [Switched.toString().toLowerCase()],
                name: `•\u00A0${Switched}`,
                className: "account-selector-service-status switched"
            },
        red:
            {
                serviceStatus: [PendingSwitchOut.toString().toLowerCase()],
                name: "Pending\u00A0Closure",
                className: "account-selector-service-status pending-closure"
            },
        grey:
            {
                serviceStatus: [
                    AccountConsolidation.toString().toLowerCase(),
                    AccountClosedToWorkAroundSystem.toString().toLowerCase(),
                    AccountDisconnectedNonPayment.toString().toLowerCase(),
                    Closed.toString().toLowerCase()
                ],
                name: Closed,
                className: "account-selector-service-status closed"
            },
        orange:
            {
                serviceStatus: undefined,
                name: "•\u00A0Processing",
                className: "account-selector-service-status processing"
            }
    }
    
    const { screen } = window;

    const navigate = useNavigate();

    let { pathname } = useLocation();

    const goBack = () => {
        pathname = pathname.endsWith('/')
            ? pathname.substring(0, pathname.length - 1)
            : pathname;

        pathname = pathname.startsWith('/')
            ? pathname.substring(1, pathname.length - 1)
            : pathname;

        if (!pathname) {
            return;
        }

        var splitted = pathname.split('/');

        splitted.pop();

        navigate(`/${splitted.join('/')}`);
    }

    const logout = async () => {
        if (user?.isImpersonatedUser) {
            window.close();
            return;
        }

        dispatch(unsetUser());
        await authService.logout();
        navigate('/login', { replace: true });
    }

    const changeCurrentAccount = (accountId: number) => {
        dispatch(updateCurrentAccount(accountId));
    };
    
    const accountStatusBadge = (status: EuropaAccountServiceStatus) => {
        const result = 
            Object.values(statusMap)
                .find(x => x.serviceStatus?.includes(status.toLowerCase())) || statusMap.orange;
        
        return <span className={result.className}>
            {result.name.toUpperCase()}
        </span>
    }

    const filteredAccountData = () => {
        const accountData = user?.accounts;
        if (!accountData) {
            return undefined;
        }

        const result = accountData.map((account, index) => {
            // if search result doesn't match, return nothing
            if (
                !account.accountNumber.toLowerCase()?.includes(searchText.toLowerCase()) && // search for account number 
                !account.services?.filter(x => x.siteAddress.toLowerCase().includes(searchText.toLowerCase())).length && // search for site address
                searchText // search text not empty
            ) {
                return;
            }
            
            return <div key={index} className={styles[
                account.accountId === currentAccount?.accountId
                    ? 'account-selector-dropdown-current'
                    : 'account-selector-dropdown'
                ]} onClick={() =>
            {
                changeCurrentAccount(account.accountId)
                setAccountSelectModalVisible(false)
                setSearchText("")
            }}>
                <div className="flex-center-left">
                    <div className={styles['account-selector-text']}>
                        Account Number:{" "}
                        {<Highlighter
                            className={styles['highlighter']}
                            searchWords={[searchText]}
                            textToHighlight={account.accountNumber}
                        />}
                    </div>
                </div>
                <div>
                    {renderServiceUnderAccount(
                        account.services?.filter(x => x.serviceType === ServiceType.Power),
                        account.services?.filter(x => x.serviceType === ServiceType.Gas)
                    )}
                </div>
            </div>
        })
        
        // no result found
        if (result?.filter(x => x !== undefined)?.length === 0)
        {
            return <div>No result found.</div>
        }
        return result
    }
    
    const renderServiceUnderAccount = (powerService?: AccountServiceInfo[], gasService?: AccountServiceInfo[]): React.ReactNode => {
        const powerServiceSorted = powerService?.sort(x => x.status === Switched ? -1 : 1);
        const gasServiceSorted = gasService?.sort(x => x.status === Switched ? -1 : 1);

        return <>
            {(powerServiceSorted?.length || 0) > 0 ?
                <div style={{ marginTop: 10 }}>
                    <div>
                        <img className={styles['journey-key-icon']} src={powerIcon} alt="pink-circle-power-icon" style={{ height: 20, marginRight: 3 }} />
                        <span className={styles["power-text"]}>Electricity</span>
                    </div>
                    <div>
                        {
                            powerServiceSorted?.map((service, index) =>
                                <div key={index} className={styles['account-selector-service-address']}>
                                    <span className={styles[service?.status === Closed
                                        ? "account-selector-service-address-inactive"
                                        : "account-selector-service-address-active"]}
                                    >
                                        <Highlighter
                                            className={styles['highlighter']}
                                            searchWords={[searchText]}
                                            textToHighlight={service.siteAddress}
                                        />
                                    </span>
                                    {accountStatusBadge(service?.status)}
                                </div>
                            )
                        }
                    </div>
                </div> : undefined
            }
            {(gasServiceSorted?.length || 0) > 0 ?
                <div style={{ marginTop: 10 }}>
                    <div>
                        <img className={styles['journey-key-icon']} src={gasIcon} alt="pink-circle-gas-icon" style={{ height: 20, marginRight: 3 }} />
                        <span className={styles["gas-text"]}>Gas</span>
                    </div>
                    <div>
                        {
                            gasServiceSorted?.map((service, index) =>
                                <div key={index} className={styles['account-selector-service-address']}>
                                    <span className={styles[service?.status === Closed
                                        ? "account-selector-service-address-inactive"
                                        : "account-selector-service-address-active"]}
                                    >
                                        <Highlighter
                                            className={styles['highlighter']}
                                            searchWords={[searchText]}
                                            textToHighlight={service.siteAddress}
                                        />
                                    </span>
                                    {accountStatusBadge(service?.status)}
                                </div>
                            )
                        }
                    </div>
                </div> : undefined
            }
        </>
    }

    const accountSelection = useMemo(() => {
        if (!user?.accounts?.length) {
            return [];
        }

        const powerService = currentAccount?.services?.filter(x => x.serviceType === ServiceType.Power);
        const gasService = currentAccount?.services?.filter(x => x.serviceType === ServiceType.Gas);

        const expandedContent = renderServiceUnderAccount(powerService, gasService);
        
        const displayContent = 
            <div className={styles['account-selector']}>
                <div className="flex-center-center">
                    <div className={styles['account-selector-text']}>
                        <FontAwesomeIcon icon={faHouse} style={{ marginLeft: 5, marginRight: 5 }} />
                        Account Number: {currentAccount?.accountNumber}
                    </div>
                    <Popover
                        className="flex-center-center"
                        trigger={isMobileAndTablet() ? "click" : "hover"} // mobile click, desktop hover 
                        placement="bottom"
                        autoAdjustOverflow={false}
                        content={expandedContent}
                        overlayInnerStyle={{ borderRadius: 20 }}
                    >
                        {
                            (powerService?.length || 0) > 0 ?
                            <div className={styles['account-selector-service-type']}>
                                <img className={styles['journey-key-icon']} src={powerIcon} alt="pink-circle-power-icon" style={{ height: 20, marginRight: 3 }} />
                                <span className={styles["power-text"]}>{screen.width > 450 ? "Electricity" : ""} {powerService?.length}</span>
                            </div> : undefined
                        }
                        {
                            (gasService?.length || 0) > 0 ?
                            <div className={styles['account-selector-service-type']}>
                                <img className={styles['journey-key-icon']} src={gasIcon} alt="pink-circle-gas-icon" style={{ height: 20, marginRight: 3 }} />
                                <span className={styles["gas-text"]}>{screen.width > 450 ? "Gas" : ""} {gasService?.length}</span>
                            </div> : undefined
                        }
                    </Popover>
                </div>
                {
                    user?.accounts?.length > 1 ? 
                        <div className={styles['account-selector-switch-account']} onClick={() => {setAccountSelectModalVisible(true)}}>
                            Switch Account
                        </div> : undefined
                }
            </div>
        
        return <div className="flex-center-center">
            {displayContent}
        </div>
    },[currentAccount?.accountNumber])

    return <>
        <div>
            {
                props.hideLogout === true ? '' :
                    <div className={styles['logout-block']}>
                        <button className={classNames("globird-button-default", styles['logout-button'])} onClick={logout}>
                            <FontAwesomeIcon
                                icon={faPowerOff}
                                style={{ marginRight: '10px' }} />Logout
                        </button>
                    </div>
            }
            <div className={styles['my-account-banner']} style={navbarBgColor}>
                <p className={styles['banner-text']}>
                    <span onClick={() => navigate('/dashboard')}>
                        <img src={myAccountBanner} alt="banner-icon" />My Account
                    </span>
                </p>
                <p className={styles['title']}>{props.title ? props.title : "Take control of your details, bills and usage"}</p>
                <p className={styles['subtitle']}>{props.subtitle ? props.subtitle : ""}</p>
            </div>
        </div>
        <div>
            {
                props.hideAccountSelection === true
                    ? <></>
                    : accountSelection
            }
        </div>
        <div>
            {props.children}
        </div>
        <div>
            {
                props.hideReturnDashboard === true ? "" :
                    <div className={styles['dashboard-return-block']}>
                        <div className={styles['dashboard-return']} onClick={goBack}>
                            <FontAwesomeIcon
                                icon={faChevronLeft}
                                style={{ marginLeft: '13px' }}
                            />
                            <span style={{
                                display: 'inline-block',
                                position: 'relative',
                                left: '15px'
                            }}>Back</span>
                        </div>
                    </div>
            }
        </div>
        <Modal
            title={
                <>
                    <div>Select Account</div>
                    <Search 
                        className={styles["search-bar"]}
                        placeholder="Search address or account number to switch account..."
                        allowClear
                        value={searchText}
                        size={"large"}
                        onChange={(x) => setSearchText(x.target.value)}
                    />
                </>
            }
            visible={accountSelectModalVisible}
            onCancel={() => {
                setAccountSelectModalVisible(false)
                setSearchText("")
            }}
            className={styles["account-selector-modal"]}
            width={650} // set hard width to prevent modal from resizing
            bodyStyle={{
                maxHeight: 'calc(100vh - 250px)',
                overflowY: "auto"
            }}
            footer={null}
        >
            {filteredAccountData()}
        </Modal>
    </>
}

const UnRoutedLayout = (props: React.PropsWithChildren<LayoutPropsType>) => <div>
    <div className={styles['my-account-banner']}>
        <p className={styles['banner-text']}>
            <span>
                <img src={myAccountBanner} alt="banner-icon" />My Account
            </span>
        </p>
        <p className={styles['title']}>{props.title ? props.title : "Take control of your details, bills and usage"}</p>
        <p className={styles['subtitle']}>{props.subtitle ? props.subtitle : ""}</p>
    </div>
</div>;

export const Layout = (props: React.PropsWithChildren<LayoutPropsType>) => {
    const routed = props.routed ?? true;

    return (
        <div className={
            classNames({
                [styles['layout']]: true,
                [styles['globird-background']]: !!props.showBackgroundImage
            })}>
            {routed ? <RoutedLayout {...props} /> : <UnRoutedLayout />}
            <div style={{ height: '42px' }}></div>
            <div style={{ position: 'absolute', bottom: 0, width: '100%' }}>
                <div style={{
                    minWidth: '300px',
                    maxWidth: '600px',
                    margin: 'auto',
                    display: 'flex',
                    justifyContent: 'space-around',
                    padding: '0 20px 20px 20px',
                    height: '42px'
                }}>
                    <a target="_blank" href={links.corporateWebsite.privacyPolicy} rel="noreferrer">Privacy Policy</ a>
                    <a target="_blank" href={links.corporateWebsite.legal} rel="noreferrer">Terms of Use</a>
                    <a target="_blank" href={links.corporateWebsite.myAccountFaqSection} rel="noreferrer">FAQs</ a>
                </div>
            </div>
        </div>
    );
}

export default Layout;